'use client';

import { Trans, t } from '@lingui/macro';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode } from '@prismo-io/schemas';
import { type FC, useCallback, useMemo } from 'react';
import {
  AiFillFacebook,
  AiFillLinkedin,
  AiFillTwitterSquare,
  AiOutlineLink,
} from 'react-icons/ai';
import type { UserSidebarSchemaT } from '../../../utils/fetch-user-sidebar';

type Props = {
  user: UserSidebarSchemaT;
};

const baseText =
  'Je viens de créer mon profil professionnel en 3D sur PRISMO !';

export const SocialNetworksShare: FC<Props> = (props: Props) => {
  const { user } = props;

  const profileUrl = useMemo(() => {
    return translate({
      app: Application.APP,
      path: '/profil/:user',
      params: {
        user: user.url,
      },
      addHostname: true,
      locale: LanguageCode.Enum.fr,
    });
  }, [user]);

  const facebookUrl = useMemo(() => {
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      profileUrl
    )}`;
  }, [profileUrl]);

  const linkedInUrl = useMemo(() => {
    return `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
      profileUrl
    )}&title=${encodeURIComponent(
      'Découvrez le profil de ' + user.full_name + '| PRISMO'
    )}&summary=${baseText}&source=${encodeURIComponent(profileUrl)}`;
  }, [profileUrl, user]);

  const twitterUrl = useMemo(() => {
    const hashTagsTwitter =
      'personnalité,lecvestmort,cvoriginal,softskills,compétences,prismo,profilprofessionnel';

    return `https://twitter.com/intent/tweet?text=${baseText.replace(
      'PRISMO',
      '@prismo_io'
    )}&url=${encodeURIComponent(profileUrl)}&hashtags=${hashTagsTwitter}`;
  }, [profileUrl, user]);

  const copyUrlToClipboard = useCallback(() => {
    navigator.clipboard.writeText(profileUrl);
  }, [profileUrl]);

  return (
    <Box component="div">
      <Box
        component="div"
        position="relative"
        width="fit-content"
        paddingX="1rem"
        paddingY=".5rem"
        marginX="auto"
        bgcolor="background.primary"
        borderRadius={1_000_000}
      >
        <Box
          component="div"
          sx={{
            position: 'absolute',
            height: '1rem',
            width: '2rem',
            top: '100%',
            left: '2rem',
            transform: 'rotate(29deg) translate(-20%,-50%)',
            background: 'inherit',
          }}
        />
        <Typography
          position="relative"
          color="primary.main"
          fontWeight="bolder"
        >
          <Trans>Partager le profil</Trans>
        </Typography>
      </Box>
      <Tooltip
        title={
          user.is_private
            ? t`Le partage est impossible car le compte est privé`
            : null
        }
        placement="top"
      >
        <Stack
          direction="row"
          justifyContent="space-evenly"
          spacing={2}
          marginY="1rem"
        >
          <Tooltip
            title={user.is_private ? '' : t`Partagez cette page sur Facebook`}
            placement="top"
          >
            <Box component="span">
              <IconButton
                disabled={user.is_private}
                href={facebookUrl}
                target="_blank"
                aria-label="facebook"
                color="primary"
              >
                <AiFillFacebook />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip
            title={user.is_private ? '' : t`Partagez cette page sur LinkedIn`}
            placement="top"
          >
            <Box component="span">
              <IconButton
                disabled={user.is_private}
                href={linkedInUrl}
                target="_blank"
                aria-label="linkedin"
                color="primary"
              >
                <AiFillLinkedin />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip
            title={
              user.is_private ? '' : t`Partagez cette page sur X (twitter)`
            }
            placement="top"
          >
            <Box component="span">
              <IconButton
                disabled={user.is_private}
                href={twitterUrl}
                target="_blank"
                aria-label="twitter"
                color="primary"
              >
                <AiFillTwitterSquare />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip
            title={
              user.is_private
                ? ''
                : t`Cliquez ici pour copiez l'Url de cette page et ainsi pouvoir la partager`
            }
            placement="top"
          >
            <Box component="span">
              <IconButton
                disabled={user.is_private}
                aria-label="url"
                color="primary"
                onClick={copyUrlToClipboard}
              >
                <AiOutlineLink />
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>
      </Tooltip>
    </Box>
  );
};
